import { TYPING_WINDOW_MS, type BusyProbe } from './autoUpdateModel';

// D-341 (3): DOM probes for "is the user in the middle of something".
let lastKeyAt = 0;
let listening = false;

/** Start remembering the last key press (idempotent). */
export function watchTyping(): void {
  if (listening || typeof window === 'undefined') return;
  listening = true;
  window.addEventListener('keydown', () => { lastKeyAt = Date.now(); }, { capture: true, passive: true });
}

function focusedFieldHasText(): boolean {
  const el = document.activeElement as HTMLElement | null;
  if (!el) return false;
  if (el.isContentEditable) return (el.textContent || '').trim().length > 0;
  if (el instanceof HTMLTextAreaElement) return el.value.trim().length > 0;
  if (el instanceof HTMLInputElement) {
    const t = el.type;
    if (['checkbox', 'radio', 'button', 'submit', 'reset', 'range', 'color', 'file'].includes(t)) return false;
    return el.value.trim().length > 0;
  }
  return false;
}

/** A form with unsaved changes registers a `beforeunload` guard; a cancelable synthetic event shows whether anyone objects. */
function unsavedGuardActive(): boolean {
  try {
    const ev = new Event('beforeunload', { cancelable: true });
    window.dispatchEvent(ev);
    return ev.defaultPrevented;
  } catch {
    return false;
  }
}

/** FIX-AUTO-REFRESH: only free text the user wrote counts. Dates, months, numbers and search/filter boxes are not a draft. */
const FREE_TEXT_INPUTS = new Set(['text', 'search']);
const FILTER_SELECTOR = '[data-filter], [role="search"], [role="searchbox"]';

/**
 * QA-PSCOPE-ENGAGE + FIX-AUTO-REFRESH: a comment half-written (textarea / contenteditable, or a text input whose value
 * differs from its defaultValue) counts, focused or not. Filter / search fields and date, month, time, number, email,
 * tel inputs never count, so a normal page is not "in the middle of work".
 */
export function anyFieldHasText(root: ParentNode = document): boolean {
  const fields = root.querySelectorAll<HTMLElement>('textarea, input, [contenteditable=""], [contenteditable="true"]');
  for (const el of Array.from(fields)) {
    if (el.closest(FILTER_SELECTOR)) continue;
    // FIX-AUTO-REFRESH: a controlled input keeps defaultValue in sync, so a marked draft field counts whenever it has a value.
    if (el.hasAttribute?.('data-draft') && (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement)) {
      if (!el.readOnly && !el.disabled && el.value.trim()) return true;
      continue;
    }
    if (el instanceof HTMLTextAreaElement) {
      if (!el.readOnly && !el.disabled && el.value.trim()) return true;
    } else if (el instanceof HTMLInputElement) {
      if (FREE_TEXT_INPUTS.has(el.type) && !el.readOnly && !el.disabled && el.value.trim() && el.value !== el.defaultValue) return true;
    } else if ((el.textContent || '').trim()) {
      return true;
    }
  }
  return false;
}

export function probeBusy(): BusyProbe {
  return {
    overlayOpen: !!document.querySelector('[data-overlay-layer] [aria-modal="true"]'),
    typing: focusedFieldHasText() || Date.now() - lastKeyAt < TYPING_WINDOW_MS,
    unsavedChanges: unsavedGuardActive(),
    fieldsWithText: anyFieldHasText(),
  };
}
